export const esc = (s) => {
  return (s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;')
    .replace(/>/g, '&gt;').replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

export const escH = (s) => {
  const d = document.createElement('div');
  d.textContent = s;
  return d.innerHTML;
};

export const formatOwnerText = (s) => {
  let t = esc(s);
  t = t.replace(/(&lt;br\s*\/?&gt;\s*){1,}/gi, '\n');
  t = t.replace(/\n{3,}/g, '\n\n');
  return t.replace(/\n/g, '<br>');
};

export const stars = (r) => {
  r = Math.round(r || 0);
  return '\u2605'.repeat(r) + '\u2606'.repeat(Math.max(0, 5 - r));
};

/**
 * Upload a file via XHR with progress tracking.
 * @param {string} url - Presigned PUT URL
 * @param {File} file - File to upload
 * @param {string} contentType - MIME type
 * @param {function} onProgress - Called with 0-100 percentage
 * @returns {Promise<void>}
 */
export const uploadWithProgress = (url, file, contentType, onProgress) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url, true);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload failed: ${xhr.status}`));
    };
    xhr.onerror = () => { reject(new Error('Network error')); };
    xhr.send(file);
  });
};

/**
 * Create a thumbnail upload placeholder with progress bar overlay.
 * If a File is provided, shows a blurred local preview behind the bar.
 * @param {HTMLElement} container - Grid/flex container to append the thumbnail into
 * @param {File} [file] - Optional file for local image preview
 * @returns {{ update: function(number), done: function(string=), error: function(string=), el: HTMLElement }}
 */
export const createProgressBar = (container, file) => {
  const wrapper = document.createElement('div');
  wrapper.className = 'relative rounded-lg overflow-hidden border border-slate-200 bg-slate-100 flex-shrink-0';
  wrapper.style.cssText = 'width:100%;aspect-ratio:4/3;min-height:80px;max-height:160px;';

  // Local preview as blurred background
  let previewHtml = '';
  if (file && file.type && file.type.startsWith('image/')) {
    const blobUrl = URL.createObjectURL(file);
    previewHtml = `<img src="${blobUrl}" class="absolute inset-0 w-full h-full object-cover opacity-40 blur-sm" onload="window.URL.revokeObjectURL(this.src)">`;
  }

  wrapper.innerHTML = `${previewHtml}<div class="absolute inset-0 bg-black/30 flex flex-col items-center justify-center p-3 gap-1.5"><span class="loading loading-spinner loading-sm text-white upload-spinner"></span><progress class="progress progress-success w-full max-w-[100px] h-1.5 upload-bar" value="0" max="100"></progress><span class="upload-lbl text-[10px] font-semibold text-white drop-shadow">0%</span></div>`;

  container.appendChild(wrapper);

  const bar = wrapper.querySelector('.upload-bar');
  const label = wrapper.querySelector('.upload-lbl');
  const spinner = wrapper.querySelector('.upload-spinner');

  return {
    el: wrapper,
    update: (pct) => {
      bar.value = pct;
      label.textContent = `${pct}%`;
    },
    done: (finalUrl) => {
      if (spinner) spinner.remove();
      bar.value = 100;
      label.textContent = '';
      if (finalUrl) {
        wrapper.innerHTML = `<img src="${finalUrl}" class="w-full h-full object-cover"><div class="absolute inset-0 bg-success/20 flex items-center justify-center transition-opacity" style="animation:fadeOut 1s forwards"><svg class="w-6 h-6 text-white drop-shadow" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5"/></svg></div>`;
      }
      setTimeout(() => { wrapper.remove(); }, 1200);
    },
    error: (msg) => {
      if (spinner) spinner.remove();
      bar.classList.remove('progress-success');
      bar.classList.add('progress-error');
      bar.value = 100;
      label.textContent = msg || 'Failed';
      label.classList.add('text-error-content');
      wrapper.classList.add('border-error');
      setTimeout(() => { wrapper.remove(); }, 3000);
    }
  };
};

/**
 * Show a modal alert (replaces window.alert).
 * @param {string} message
 * @param {string} [type] - 'error' | 'success' | 'info' (default: 'info')
 * @returns {Promise<void>}
 */
export const showAlert = (message, type) => {
  return new Promise((resolve) => {
    const btnMap = { error: 'btn-error', success: 'btn-success', info: 'btn-neutral' };
    const alertMap = { error: 'alert-error', success: 'alert-success', info: 'alert-info' };
    const t = type || 'info';
    const overlay = document.createElement('div');
    overlay.className = 'modal modal-open modal-bottom sm:modal-middle';
    overlay.style.zIndex = '9999';
    overlay.innerHTML = `<div class="modal-box"><div class="alert ${alertMap[t] || alertMap.info} mb-4"><span>${esc(message)}</span></div><div class="modal-action"><button class="btn ${btnMap[t] || btnMap.info} btn-sm modal-ok">OK</button></div></div><form method="dialog" class="modal-backdrop"><button class="modal-ok">close</button></form>`;
    document.body.appendChild(overlay);
    const close = () => { overlay.remove(); resolve(); };
    overlay.querySelectorAll('.modal-ok').forEach((b) => { b.addEventListener('click', close); });
  });
};

/**
 * Show a modal confirm dialog (replaces window.confirm).
 * @param {string} message
 * @param {object} [opts]
 * @param {string} [opts.confirmText] - Button text (default: 'Confirm')
 * @param {string} [opts.confirmClass] - Button class (default: red for destructive)
 * @returns {Promise<boolean>}
 */
export const showConfirm = (message, opts) => {
  opts = opts || {};
  return new Promise((resolve) => {
    const btnClass = opts.confirmClass || 'btn-error';
    const btnText = opts.confirmText || 'Confirm';
    const overlay = document.createElement('div');
    overlay.className = 'modal modal-open modal-bottom sm:modal-middle';
    overlay.style.zIndex = '9999';
    overlay.innerHTML = `<div class="modal-box"><h3 class="font-bold text-lg">Are you sure?</h3><p class="py-4 text-sm">${esc(message)}</p><div class="modal-action"><button class="btn btn-ghost btn-sm modal-cancel">Cancel</button><button class="btn ${btnClass} btn-sm modal-confirm">${esc(btnText)}</button></div></div><form method="dialog" class="modal-backdrop"><button class="modal-cancel">close</button></form>`;
    document.body.appendChild(overlay);
    const close = (val) => { overlay.remove(); resolve(val); };
    overlay.querySelector('.modal-confirm').addEventListener('click', () => { close(true); });
    overlay.querySelectorAll('.modal-cancel').forEach((b) => { b.addEventListener('click', () => { close(false); }); });
  });
};

export const highlight = (raw, q) => {
  if (!q) return esc(raw);
  const text = raw || '', lower = text.toLowerCase(), lq = q.toLowerCase();
  let result = '', i = 0;
  while (i < text.length) {
    const j = lower.indexOf(lq, i);
    if (j === -1) { result += esc(text.slice(i)); break; }
    result += esc(text.slice(i, j));
    result += `<mark>${esc(text.slice(j, j + q.length))}</mark>`;
    i = j + q.length;
  }
  return result;
};
